import { useState } from 'react'
import QRCode from 'qrcode'
import { FileText, Loader2, Package, Printer, Tag } from 'lucide-react'
import type {
    OrderDocumentType,
    OrderFollowupAction,
} from '@/lib/order-followup-tracking'
import type { ReactNode } from 'react'

import type { SalesOrder } from '@/lib/api/sales-order-api'
import type { PrintInfoValues } from '@/components/sections/print-info-dialog'
import { captureOrderFollowup } from '@/lib/order-followup-tracking'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { invoiceApi } from '@/lib/api/invoice-api'
import { buildBoxLabelHtml } from '@/lib/box-label'
import { printReceiptHtml } from '@/lib/print-receipt'
import { PrintInfoDialog } from '@/components/sections/print-info-dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    order?: SalesOrder
    companyName?: string
    companyPhone?: string
}

/** Một mục "in / tải chứng từ", mô tả dạng dữ liệu để render được ở hàng nút
 *  ngang (desktop) lẫn menu kebab (mobile). */
export interface InvoiceDocumentAction {
    key: string
    label: string
    icon: ReactNode
    disabled?: boolean
    title?: string
    onSelect: () => void
}

export interface InvoiceDocumentActions {
    items: InvoiceDocumentAction[]
    /** Dialog nhập thông tin nhãn thùng. Phải render NGOÀI menu kebab. */
    dialogs: ReactNode
}

export function useInvoiceDocumentActions({
    orderId,
    order,
    companyName,
    companyPhone,
}: Props): InvoiceDocumentActions {
    const { hasPermission } = useAuth()
    const [printingBoxLabel, setPrintingBoxLabel] = useState(false)
    const [boxDialogOpen, setBoxDialogOpen] = useState(false)

    const openPdf = (
        url: string,
        action: OrderFollowupAction,
        documentType: OrderDocumentType,
    ) => {
        captureOrderFollowup(orderId, action, documentType)
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    // Defaults for the box-label review dialog. The note field maps to the
    // slip's delivery_note line.
    const dialogInitial: PrintInfoValues = {
        customerName: order?.customer_name ?? '',
        customerPhone: order?.customer_phone ?? '',
        shippingAddress: order?.shipping_address ?? '',
        shippingWard: order?.shipping_ward ?? '',
        shippingProvince: order?.shipping_province ?? '',
        note: order?.delivery_note ?? '',
        codAmount: String(
            (order?.total_amount ?? 0) - (order?.paid_amount ?? 0),
        ),
        packageCount: String(order?.package_count ?? 1),
    }

    // Nhãn thùng (render ở FE) in dòng COD, nên chỉ hiện ô COD khi đơn có thu.
    const showCodField = order?.collect_cod === true

    // Ô trống → undefined để builder tự tính `total - paid` như cũ. Dùng
    // Number.isFinite chứ không phải `|| undefined`: 0 là số COD hợp lệ.
    const parseCod = (raw: string): number | undefined => {
        const trimmed = raw.trim()
        if (trimmed === '') return undefined
        const parsed = Number(trimmed)
        return Number.isFinite(parsed) ? parsed : undefined
    }

    // Số kiện ≥ 1; ô trống hoặc rác → 1 tờ như cũ.
    const parsePackageCount = (raw: string): number => {
        const parsed = Math.floor(Number(raw))
        return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1
    }

    const handleBoxDialogConfirm = (values: PrintInfoValues) => {
        captureOrderFollowup(orderId, 'print_requested', 'box_label')
        setBoxDialogOpen(false)
        void handlePrintBoxLabel(values)
    }

    const handlePrintBoxLabel = async (v: PrintInfoValues) => {
        if (!order) return
        setPrintingBoxLabel(true)
        try {
            // QR trỏ về màn chi tiết đơn hàng — giống phiếu giao hàng, để tài
            // xế/khách quét là mở luôn đơn (không phải màn xác nhận đóng gói).
            const confirmUrl = `${window.location.origin}/orders/${order.id}`
            const qrDataUrl = await QRCode.toDataURL(confirmUrl, { width: 128 })
            const html = buildBoxLabelHtml({
                customerName: v.customerName,
                customerPhone: v.customerPhone || undefined,
                shippingAddress: v.shippingAddress || undefined,
                shippingWard: v.shippingWard || undefined,
                shippingProvince: v.shippingProvince || undefined,
                shippingCarrierName: order.shipping_carrier_name || undefined,
                totalAmount: order.total_amount,
                paidAmount: order.paid_amount,
                collectCod: order.collect_cod,
                codAmount: parseCod(v.codAmount),
                shippingFeePayer: order.shipping_fee_payer,
                gift: order.gift
                    ? { shippingPrepaid: order.gift.shipping_prepaid }
                    : undefined,
                deliveryNote: v.note || undefined,
                packageCount: parsePackageCount(v.packageCount),
                companyName: companyName || undefined,
                companyPhone: companyPhone || undefined,
                qrDataUrl,
            })
            printReceiptHtml(html)
        } catch {
            // QR generation lỗi hoặc iframe bị block → nuốt lỗi, user thấy nút reset.
        } finally {
            setPrintingBoxLabel(false)
        }
    }

    // Every document endpoint here enforces invoices.read; hide the whole bar
    // when the viewer lacks it instead of letting the PDF request 403.
    const allowed = hasPermission('invoices.read')

    const items: InvoiceDocumentAction[] = allowed
        ? [
              {
                  key: 'invoice',
                  label: m.invoice_download_invoice(),
                  icon: <FileText className="size-4" />,
                  onSelect: () =>
                      openPdf(
                          invoiceApi.getInvoicePdfUrl(orderId),
                          'download_requested',
                          'invoice',
                      ),
              },
              {
                  key: 'box-label',
                  label: m.box_label_print_btn(),
                  icon: printingBoxLabel ? (
                      <Loader2 className="size-4 animate-spin" />
                  ) : (
                      <Tag className="size-4" />
                  ),
                  disabled: printingBoxLabel || !order,
                  onSelect: () => setBoxDialogOpen(true),
              },
              {
                  // PDF phiếu xuất kho cũ đã bị gỡ cùng route BE (29/06); giờ mở
                  // trang PXK theo lô lọc theo đơn — kho xem/in từ đó.
                  key: 'stock-out-note',
                  label: m.invoice_download_stock_out_note(),
                  icon: <Package className="size-4" />,
                  onSelect: () =>
                      openPdf(
                          `/sales-warehouse/stock-out-notes?sales_order_id=${orderId}`,
                          'print_requested',
                          'stock_out_note',
                      ),
              },
              {
                  key: 'packing-slip',
                  label: m.print_packing_slip(),
                  icon: <Printer className="size-4" />,
                  disabled: !order,
                  title: m.print_packing_slip_tooltip(),
                  onSelect: () =>
                      openPdf(
                          invoiceApi.getPackingSlipPdfUrl(orderId),
                          'print_requested',
                          'packing_slip',
                      ),
              },
          ]
        : []

    const dialogs = allowed ? (
        <PrintInfoDialog
            showCod={showCodField}
            showPackageCount
            open={boxDialogOpen}
            onOpenChange={setBoxDialogOpen}
            initial={dialogInitial}
            onConfirm={handleBoxDialogConfirm}
        />
    ) : null

    return { items, dialogs }
}

/**
 * Hàng nút ngang — bố cục mặc định (desktop và mọi call-site cũ).
 *
 * Màn chi tiết đơn trên mobile không dùng component này: nó gọi thẳng
 * `useInvoiceDocumentActions` để gộp chung vào menu kebab ở header.
 */
export function InvoiceDownloadActions(props: Props) {
    const { items, dialogs } = useInvoiceDocumentActions(props)

    if (items.length === 0) return null

    return (
        <div className="flex flex-wrap gap-2">
            {items.map((item) => (
                <Button
                    key={item.key}
                    size="sm"
                    variant="outline"
                    disabled={item.disabled}
                    title={item.title}
                    onClick={item.onSelect}
                >
                    {item.icon}
                    {item.label}
                </Button>
            ))}
            {dialogs}
        </div>
    )
}
